import { Injectable, signal, computed, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme =
	| 'dark'
	| 'light'
	| 'pastel'
	| 'graphite-dark'
	| 'graphite-light'
	| 'graphite-pearl'
	| 'aurora'
	| 'sunset'
	| 'mint-glass';

/** Metadata for a selectable "klimat" (visual skin). */
export interface ThemeMeta {
	/** Value written to `data-theme` + persisted. */
	id: Theme;
	/** Display name shown in the picker. */
	label: string;
	/** Short PL description shown under the swatch. */
	desc: string;
	/** Underlying color scheme (drives icon / chart palette fallbacks). */
	scheme: 'dark' | 'light';
	/**
	 * Representative colors for the picker swatch (independent of CSS vars).
	 * `bg` and `accent` are fed straight to `background`, so a gradient theme puts its
	 * gradient here and the swatch shows the real thing rather than a flat stand-in.
	 */
	swatch: { bg: string; surface: string; accent: string; text: string };
	/** True for the gradient skins — the picker groups them under their own heading. */
	gradient?: boolean;
}

/** Registry of all available themes. Order = display order in the picker. */
export const THEMES: ThemeMeta[] = [
	{
		id: 'dark',
		label: 'Cryptionis Lime',
		desc: 'Domyślny — ciemny, limonkowy akcent.',
		scheme: 'dark',
		swatch: { bg: '#0A110E', surface: '#12201A', accent: '#9DC540', text: '#E6F0E8' },
	},
	{
		id: 'pastel',
		label: 'Pastel',
		desc: 'Miękki, jasny, lawendowy klimat „soft-UI”.',
		scheme: 'light',
		swatch: { bg: '#F6F4FB', surface: '#FFFFFF', accent: '#8B6FE8', text: '#322C45' },
	},
	{
		id: 'light',
		label: 'Pastelowa zieleń',
		desc: 'Jasny motyw z pastelową, zieloną nawigacją.',
		scheme: 'light',
		swatch: { bg: '#FFFFFF', surface: '#F8FAFB', accent: '#5FA033', text: '#0F172A' },
	},
	{
		id: 'graphite-dark',
		label: 'Grafit ciemny',
		desc: 'Grafitowa szarość, ciepły bursztynowy akcent.',
		scheme: 'dark',
		swatch: { bg: '#15151A', surface: '#22222A', accent: '#E0A23C', text: '#EAEAF0' },
	},
	{
		id: 'graphite-light',
		label: 'Grafit złoty',
		desc: 'Jasny grafit z ciepłą, złotą nawigacją.',
		scheme: 'light',
		swatch: { bg: '#F5F4F2', surface: '#FFFFFF', accent: '#B97D1E', text: '#1C1B19' },
	},
	{
		id: 'graphite-pearl',
		label: 'Jasny grafit',
		desc: 'Jasny grafit z neutralną, pastelową nawigacją.',
		scheme: 'light',
		swatch: { bg: '#F2F2F4', surface: '#FFFFFF', accent: '#B97D1E', text: '#1C1B19' },
	},
	{
		id: 'aurora',
		label: 'Aurora',
		desc: 'Ciemny granat z poświatą fiolet → cyjan.',
		scheme: 'dark',
		gradient: true,
		swatch: {
			bg: 'linear-gradient(150deg, #1B1B45 0%, #0B0B1A 60%, #082C36 100%)',
			surface: '#1D1E42',
			accent: 'linear-gradient(135deg, #7C6CFF, #22D3EE)',
			text: '#E8E9FF',
		},
	},
	{
		id: 'sunset',
		label: 'Zachód',
		desc: 'Ciemny śliwkowy zmierzch, magenta → bursztyn.',
		scheme: 'dark',
		gradient: true,
		swatch: {
			bg: 'linear-gradient(150deg, #3E1B37 0%, #150D14 58%, #3A1F14 100%)',
			surface: '#311C31',
			accent: 'linear-gradient(135deg, #F472B6, #FB923C)',
			text: '#FDEBEF',
		},
	},
	{
		id: 'mint-glass',
		label: 'Mięta',
		desc: 'Jasny, chłodny motyw z poświatą mięta → błękit.',
		scheme: 'light',
		gradient: true,
		swatch: {
			bg: 'linear-gradient(150deg, #DFF4F1 0%, #F4FBFA 55%, #DCEEFB 100%)',
			surface: '#FFFFFF',
			accent: 'linear-gradient(135deg, #14B8A6, #38BDF8)',
			text: '#0F2E2C',
		},
	},
];

const THEME_IDS = THEMES.map((t) => t.id);

@Injectable({ providedIn: 'root' })
export class ThemeService {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly storageKey = 'cr-theme';

	readonly theme = signal<Theme>(this.loadTheme());

	/** All selectable themes (for the Settings picker). */
	readonly themes = THEMES;

	/** Underlying scheme of the active theme — use this instead of `theme() === 'dark'`. */
	readonly scheme = computed<'dark' | 'light'>(
		() => THEMES.find((t) => t.id === this.theme())?.scheme ?? 'dark',
	);

	/** True when the active theme uses a dark scheme. */
	readonly isDark = computed(() => this.scheme() === 'dark');

	constructor() {
		if (isPlatformBrowser(this.platformId)) {
			effect(() => {
				const t = this.theme();
				document.documentElement.setAttribute('data-theme', t);
				localStorage.setItem(this.storageKey, t);
			});
		}
	}

	/** Set a specific theme by id (ignores unknown ids). */
	setTheme(id: Theme): void {
		if (THEME_IDS.includes(id)) {
			this.theme.set(id);
		}
	}

	/** Cycle through the registry in order (used by the header/sidenav quick button). */
	toggle(): void {
		this.theme.update((current) => {
			const idx = THEME_IDS.indexOf(current);
			return THEME_IDS[(idx + 1) % THEME_IDS.length];
		});
	}

	private loadTheme(): Theme {
		if (isPlatformBrowser(this.platformId)) {
			const stored = localStorage.getItem(this.storageKey) as Theme | null;

			if (stored && THEME_IDS.includes(stored)) {
				return stored;
			}
		}

		// Default to the brand dark theme (do NOT auto-follow the OS light/dark preference — the app
		// is dark-first; the user can switch to light manually, which persists in `cr-theme`).
		return 'dark';
	}
}
